import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CompanyInfo } from 'src/app/shared/models/company';

export class ProfitLossPdfGenerator {
  generatePdf(data: any, companyInfo: CompanyInfo, period: string, startDate: string, endDate: string): jsPDF {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'letter'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    let yPosition = 40;

    // Header - Company Name
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(companyInfo?.company_name || 'Profit & Loss Report', margin, yPosition);
    yPosition += 25;

    // Report Subtitle
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Profit & Loss Statement', margin, yPosition);
    
    // Period Label
    const periodStr = period === 'Custom' ? `${startDate} to ${endDate}` : period;
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Duration: ${periodStr}`, margin, yPosition + 15);
    
    yPosition += 50;

    // Summary Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text('Summary Overview', margin, yPosition);
    yPosition += 15;

    const summaryRows = [
      ['Total Revenue', `$${(data.totalRevenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Cost of Goods Sold', `$${(data.totalCogs || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Gross Profit', `$${(data.grossProfit || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Total Expenses', `$${(data.totalExpenses || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Net Income', `$${(data.netIncome || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`]
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [['Metric', 'Amount']],
      body: summaryRows,
      margin: { left: margin },
      styles: { fontSize: 10 },
      headStyles: { fillColor: [44, 62, 80] },
      columnStyles: {
        1: { halign: 'right', fontStyle: 'bold' }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 40;

    // Income Table
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Income Breakdown', margin, yPosition);
    yPosition += 15;

    const incomeRows = data.incomeAccounts.map((acc: any) => [
      acc.name,
      `$${(acc.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Account', 'Total']],
      body: incomeRows,
      margin: { left: margin },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [0, 168, 255] },
      columnStyles: {
        1: { halign: 'right' }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 40;

    // Check if we need a new page for Expenses
    if (yPosition > doc.internal.pageSize.getHeight() - 100) {
      doc.addPage();
      yPosition = 40;
    }

    // Expenses Table
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Expense Breakdown', margin, yPosition);
    yPosition += 15;

    const expenseRows = data.expenseAccounts.map((acc: any) => [
      acc.name,
      `$${(acc.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Account', 'Total']],
      body: expenseRows,
      margin: { left: margin },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [255, 77, 77] },
      columnStyles: {
        1: { halign: 'right' }
      }
    });

    // Footer
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Generated on ${new Date().toLocaleString()} - Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'center' }
      );
    }

    return doc;
  }
}
